import { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useHospitalStore } from '@/stores/hospitalStore'

const prefectures = [
  '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
  '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
  '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
  '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県',
]

export default function HospitalEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'create' || !id
  
  const { getHospital, addHospital, updateHospital } = useHospitalStore()
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'クリニック',
    postalCode: '',
    prefecture: '',
    city: '',
    address: '',
    tel: '',
    email: '',
    representative: '',
    status: 'active' as 'active' | 'inactive',
  })

  useEffect(() => {
    if (!isNew && id) {
      const hospital = getHospital(id)
      if (hospital) {
        // 住所を分解
        const prefecture = prefectures.find(p => hospital.address.startsWith(p)) || ''
        const restAddress = hospital.address.replace(prefecture, '').trim()
        const cityMatch = restAddress.match(/^[^\d]+/)
        const city = cityMatch ? cityMatch[0] : ''
        const address = restAddress.replace(city, '').trim()

        setFormData({
          name: hospital.name,
          type: hospital.type,
          postalCode: hospital.postalCode,
          prefecture: prefecture,
          city: city,
          address: address,
          tel: hospital.tel,
          email: hospital.email,
          representative: hospital.representative,
          status: hospital.status,
        })
      }
    }
  }, [id, isNew, getHospital])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    const fullAddress = `${formData.prefecture}${formData.city}${formData.address}`
    
    if (isNew) {
      addHospital({
        name: formData.name,
        type: formData.type,
        postalCode: formData.postalCode,
        address: fullAddress,
        tel: formData.tel,
        email: formData.email,
        representative: formData.representative,
        status: formData.status,
      })
    } else if (id) {
      updateHospital(id, {
        name: formData.name,
        type: formData.type,
        postalCode: formData.postalCode,
        address: fullAddress,
        tel: formData.tel,
        email: formData.email,
        representative: formData.representative,
        status: formData.status,
      })
    }
    
    navigate('/master/hospitals')
  }

  return (
    <>
      <header className="aip-main__header">
        <div className="aip-main__header-container">
          <Link to="/master/hospitals" className="aip-button aip-button--icon">
            <span className="material-symbols-outlined">arrow_back</span>
          </Link>
          <h1 className="aip-main__header-title">
            病院 {isNew ? '新規登録' : '編集'}
          </h1>
        </div>
      </header>

      <div className="aip-main__content">
        <form onSubmit={handleSubmit} className="form-container">
          <div className="form-header">
            <h2 className="form-title">{isNew ? '新規病院を登録' : '病院情報を編集'}</h2>
            <p className="form-description">
              病院の基本情報を入力してください
            </p>
          </div>

          <div className="form-body">
            {/* 基本情報 */}
            <section className="form-section">
              <h3 className="form-section__title">基本情報</h3>
              <div className="form-grid">
                <div className="aip-floating-field aip-floating-field--full">
                  <label className="aip-floating-field__label" htmlFor="name">
                    病院名<span className="required">※必須</span>
                  </label>
                  <input
                    className="aip-input-text"
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="例：Baseクリニック赤坂"
                    required
                  />
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="type">
                    種別<span className="required">※必須</span>
                  </label>
                  <select
                    className="aip-select"
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                  >
                    <option value="クリニック">クリニック</option>
                    <option value="研究機関">研究機関</option>
                    <option value="事務局">事務局</option>
                  </select>
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="representative">
                    代表者名<span className="required">※必須</span>
                  </label>
                  <input
                    className="aip-input-text"
                    type="text"
                    id="representative"
                    name="representative"
                    value={formData.representative}
                    onChange={handleChange}
                    placeholder="例：吉岡 紘平"
                    required
                  />
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label">
                    状態
                  </label>
                  <select
                    className="aip-select"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="active">有効</option>
                    <option value="inactive">無効</option>
                  </select>
                </div>
              </div>
            </section>

            {/* 所在地 */}
            <section className="form-section">
              <h3 className="form-section__title">所在地</h3>
              <div className="form-grid">
                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="postalCode">
                    郵便番号
                  </label>
                  <input
                    className="aip-input-text"
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    placeholder="例：107-0052"
                  />
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="prefecture">
                    都道府県<span className="required">※必須</span>
                  </label>
                  <select
                    className="aip-select"
                    id="prefecture"
                    name="prefecture"
                    value={formData.prefecture}
                    onChange={handleChange}
                    required
                  >
                    <option value="">選択してください</option>
                    {prefectures.map((pref) => (
                      <option key={pref} value={pref}>{pref}</option>
                    ))}
                  </select>
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="city">
                    市区町村
                  </label>
                  <input
                    className="aip-input-text"
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="例：港区"
                  />
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="address">
                    番地・建物名
                  </label>
                  <input
                    className="aip-input-text"
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="例：赤坂1-1-1 赤坂ビル3F"
                  />
                </div>
              </div>
            </section>

            {/* 連絡先 */}
            <section className="form-section">
              <h3 className="form-section__title">連絡先</h3>
              <div className="form-grid">
                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="tel">
                    電話番号<span className="required">※必須</span>
                  </label>
                  <input
                    className="aip-input-text"
                    type="tel"
                    id="tel"
                    name="tel"
                    value={formData.tel}
                    onChange={handleChange}
                    placeholder="例：03-1234-5678"
                    required
                  />
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="email">
                    メールアドレス<span className="required">※必須</span>
                  </label>
                  <input
                    className="aip-input-text"
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="例：info@example.jp"
                    required
                  />
                </div>
              </div>
            </section>
          </div>

          {/* フッター */}
          <div className="form-footer">
            <Link to="/master/hospitals" className="aip-button aip-button--outline">
              キャンセル
            </Link>
            <button type="submit" className="aip-button aip-button--solid-fill">
              <span className="material-symbols-outlined">save</span>
              {isNew ? '登録' : '保存'}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .form-container {
          background-color: var(--background-primary);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
        }

        .form-header {
          padding: var(--spacing-lg);
          border-bottom: 1px solid var(--border-color);
        }

        .form-title {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: var(--spacing-xs);
        }

        .form-description {
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .form-body {
          padding: var(--spacing-lg);
        }

        .form-section {
          margin-bottom: var(--spacing-xl);
        }

        .form-section__title {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: var(--spacing-md);
          padding-bottom: var(--spacing-sm);
          border-bottom: 1px solid var(--border-color);
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--spacing-md);
        }

        .aip-floating-field {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-xs);
        }

        .aip-floating-field--full {
          grid-column: 1 / -1;
        }

        .aip-floating-field__label {
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--text-secondary);
        }

        .aip-input-text,
        .aip-select,
        .aip-textarea {
          width: 100%;
          padding: var(--spacing-sm) var(--spacing-md);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          background-color: white;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .aip-input-text:focus,
        .aip-select:focus,
        .aip-textarea:focus {
          outline: none;
          border-color: var(--color-blue500);
          box-shadow: 0 0 0 3px var(--color-blue100);
        }

        .aip-textarea {
          resize: vertical;
        }

        .required {
          color: var(--color-alert);
          font-size: 0.75rem;
          margin-left: var(--spacing-xs);
        }

        .form-footer {
          display: flex;
          justify-content: flex-end;
          gap: var(--spacing-sm);
          padding: var(--spacing-lg);
          border-top: 1px solid var(--border-color);
        }

        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  )
}
